import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";

import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { MdOutlineCancel } from "react-icons/md";

import ConfirmationModal from "../../../../../../../../components/Dialogs/ConfirmationModal";
import { Color } from "../../../../../../../../components/Form/Button";
import { StorageBoxType } from "../../../../../../../../models/storage.box.type";
import { Description, DescriptionWrapper, Warning } from "./styles";

interface DeleteStorageBoxTypeModalProps {
  onDelete: (storageBoxType?: StorageBoxType) => void;
  isDeleting?: boolean;
  hasError?: boolean;
}

export interface DeleteStorageBoxTypeHandle {
  onOpenModal: (data: StorageBoxType) => void;
  onCloseModal: () => void;
}

const ConfirmDeleteStorageBoxTypeModal: ForwardRefRenderFunction<
  DeleteStorageBoxTypeHandle,
  DeleteStorageBoxTypeModalProps
> = (props, ref) => {
  const { onDelete, isDeleting, hasError } = props;
  const { t: translate } = useTranslation();

  const [open, setOpen] = useState<boolean>(false);
  const [storageBoxType, setStorageBoxType] = useState<StorageBoxType>();

  const onOpenModal = (data: StorageBoxType) => {
    setStorageBoxType(data);
    setOpen(true);
  };

  useImperativeHandle(ref, () => ({
    onOpenModal,
    onCloseModal: () => setOpen(false),
  }));

  useEffect(() => {
    if (hasError !== undefined && !hasError) {
      setOpen(false);
    }
  }, [hasError]);

  return (
    <ConfirmationModal
      open={open}
      closeModal={() => setOpen(false)}
      type="error"
      title={translate("deleteStorageBoxType")}
      leftIconTitle={<MdOutlineCancel color="#F04438" size={20} />}
      loading={isDeleting}
      cancelDisabled={isDeleting}
      customColorCancel={Color.TEXT_INFO}
      customColorSave={Color.ERROR}
      textCancel={translate("back")}
      textSubmit={translate("delete")}
      submit={() => onDelete(storageBoxType)}
      width="520px !important"
    >
      <Grid container spacing={2} className="mt-2">
        <Grid item className="w-100">
          <DescriptionWrapper data-cy="confirmationModalCancelBody">
            <Description>
              {translate("areYouSureYouWantToDeleteThisStorageBoxType")}{" "}
              {storageBoxType?.name ? storageBoxType?.name : ""}?
            </Description>

            <Warning className="error">
              {translate("thisActionIsPermanentAndCannotBeUndone")}
            </Warning>
          </DescriptionWrapper>
        </Grid>
      </Grid>
    </ConfirmationModal>
  );
};

export default forwardRef(ConfirmDeleteStorageBoxTypeModal);
